import { useState, type ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const PROSE = 'text-[13.5px] leading-relaxed text-[#37352F] dark:text-gray-100 prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-[13.5px] prose-headings:font-semibold prose-strong:text-[#37352F] dark:prose-invert';

interface Props {
  role: 'user' | 'assistant';
  content: string;
  /** Messaggio in arrivo: mostra il cursore e nasconde "Copia". */
  streaming?: boolean;
  onInternalLink: (href: string) => void;
}

/** Un messaggio: i tuoi in un fumetto grigio a destra, le risposte come testo pulito con "Copia". */
export function AiHelpMessage({ role, content, streaming = false, onInternalLink }: Props) {
  const [copied, setCopied] = useState(false);

  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] px-3 py-2 rounded-2xl rounded-br-md bg-[#F1F1EF] dark:bg-gray-800 text-[13.5px] text-[#37352F] dark:text-gray-100 whitespace-pre-wrap break-words">{content}</p>
      </div>
    );
  }

  const Link = ({ href, children }: { href?: string; children?: ReactNode }) =>
    href?.startsWith('/') ? (
      <button type="button" onClick={() => onInternalLink(href)}
        className="inline font-medium text-[#37352F] dark:text-white underline decoration-[rgba(55,53,47,0.4)] underline-offset-2 hover:decoration-[#37352F]">
        {children}
      </button>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#37352F] dark:text-white underline underline-offset-2">{children}</a>
    );

  const copy = async () => {
    try { await navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* appunti non disponibili */ }
  };

  return (
    <div className="group">
      <div className={PROSE}>
        <ReactMarkdown components={{ a: Link as never }}>{content}</ReactMarkdown>
        {streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-text-bottom rounded-sm bg-[#37352F] dark:bg-white animate-pulse" aria-hidden="true" />}
      </div>
      {!streaming && (
        <button type="button" onClick={copy} aria-label="Copia la risposta"
          className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-[#9B9A97] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[rgba(55,53,47,0.06)] hover:text-[#37352F] transition-opacity">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied ? 'Copiato' : 'Copia'}
        </button>
      )}
    </div>
  );
}

/** Indicatore mentre l'assistente prepara la risposta. */
export function AiHelpThinking() {
  return (
    <div className="flex items-center gap-2 text-xs text-[#787774]" role="status">
      <span className="flex gap-1" aria-hidden="true">
        {[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#9B9A97] animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
      </span>
      Sto pensando…
    </div>
  );
}
